import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, Stack } from '@mui/material';
import api from '../../../conf/api';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import StyledVideoCard from '../components/StyledVideoCard';
import MultiStyledAutoComplete from '../../../Components/AutoCompletes/MultiStyledAutoComplete.js';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import {
    LibraryShell, ToolbarPaper, FieldLabel, LinkButton, VideoGridSkeleton, LibraryEmpty,
    PagerControls, PaginationFoot, GRID_SX,
} from './LibraryParts';


const Library = (props) => {
    const location = useLocation();
    const navigate = useNavigate();

    const getInitialParams = () => {
        const searchParams = new URLSearchParams(location.search);
        return {
            categories: searchParams.get('categories') || '',
            page: parseInt(searchParams.get('page'), 10) || 1,
            page_size: parseInt(searchParams.get('page_size'), 10) || 12,
        };
    };
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [videos, setVideos] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [videoLoading, setVideoLoading] = useState(false);
    const [params, setParams] = useState(getInitialParams());
    const [searchTerm, setSearchTerm] = useState(params.search || '');


    useEffect(() => {
        const fetchCategories = async () => {
            setLoading(true);
            try {
                const response = await api.get('/users/categories/');
                setCategories(response.data);
            } catch (err) {
                console.error('Failed to fetch categories:', err);
            }
            setLoading(false);
        };

        fetchCategories();
    }, []);

    const RefrashFunc = async () => {
        setVideoLoading(true);
        const _params = { ...params, downloaded: true };

        try {
            const response = await api.get('/videos/api/', {
                params: _params,
            });
            setVideos(response.data?.results || []);
            setTotalCount(response.data?.count || 0);

        } catch (err) {
            console.error('Failed to fetch videos:', err);
        }
        setVideoLoading(false);
    };

    useEffect(() => {
        const controller = new AbortController();
        const FetchVideos = async () => {
            setVideoLoading(true);
            const _params = { ...params, downloaded: true };
            try {
                const response = await api.get('/videos/api/', {
                    params: _params,
                    signal: controller.signal,
                });
                setVideos(response.data?.results || []);
                setTotalCount(response.data?.count || 0);

            } catch (err) {
                console.error('Failed to fetch videos:', err);
            }
            setVideoLoading(false);
        };
        FetchVideos();
        return () => controller.abort();
        // eslint-disable-next-line 
    }, [selected, params]);

    useEffect(() => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                searchParams.set(key, value);
            }
        });
        navigate({ search: searchParams.toString() }, { replace: true });
    }, [params, navigate]);

    // Update parms 
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const newParams = {
            categories: searchParams.get('categories') || '',
            page: parseInt(searchParams.get('page'), 10) || 1,
            page_size: parseInt(searchParams.get('page_size'), 10) || 12,
        };

        const isSame = Object.keys(newParams).every(
            key => newParams[key] === params[key]
        );

        if (!isSame) {
            setSelected(newParams.categories ? newParams.categories.split(',').map(Number) : []);
            setParams(newParams);
        }
    }, [location.search]);

    const handleCtgChange = (new_ctgs) => {
        setSelected(new_ctgs.map(c => c.id));
        const resetSearch = new_ctgs.length > 0;
        setSearchTerm(resetSearch ? "" : searchTerm);
        setParams(pre => ({
            ...pre,
            page: 1,
            categories: new_ctgs.map(c => c.id).join(","),
        }));
    };

    const FetchVideo = async () => {
        setVideoLoading(true);
        const _params = { ...params, downloaded: true };
        try {
            const response = await api.get('/videos/api/', {
                params: _params,
            });
            setVideos(response.data?.results || []);
            setTotalCount(response.data?.count || 0);
        } catch (err) {
            console.error('Failed to fetch videos:', err);
        }
        setVideoLoading(false);

    };

    const handleDownload = (video) => async () => {
        const download_link = video.dropbox_link?.replace("www.dropbox.com", "dl.dropboxusercontent.com");
        try {
            // Ask user where to save the file
            const fileHandle = await window.showSaveFilePicker({
                suggestedName: video.title || "video.mp4",
                types: [
                    {
                        description: "MP4 Video",
                        accept: { "video/mp4": [".mp4"] },
                    },
                ],
            });

            const writable = await fileHandle.createWritable();

            // Send request to save download history
            try {
                const res = await api.post("videos/downloads-history/", { video: video.id });
                if (res.status === 201) {
                    const blob = await fetch(download_link).then(res => res.blob());
                    await writable.write(blob);
                    await writable.close();
                    toast.success('Download completed!');
                    FetchVideo();
                }
            } catch (err) {
                if (err.response?.status === 400 && err.response?.data?.non_field_errors) {
                    const blob = await fetch(download_link).then(res => res.blob());
                    await writable.write(blob);
                    await writable.close();
                    toast.info('Downloading... again.');
                    FetchVideo();
                } else {
                    await writable.close();
                    toast.error('Failed to start download.');
                    console.error('Error:', err);
                }
            }
        } catch (dialogError) {
            console.warn("Save dialog was canceled or failed:", dialogError);
        }
    };


    const hasFilters = Boolean(params.categories);

    return (
        <LibraryShell
            title="Downloaded Videos"
            description="Videos you have already downloaded from the library."
            actions={
                <LinkButton startIcon={<VideoLibraryOutlinedIcon />} onClick={() => navigate('/video/library')}>
                    Video Library
                </LinkButton>
            }
        >
            <ToolbarPaper>
                {loading ? (
                    <Skeleton variant="rounded" height={56} />
                ) : categories.length > 0 && (
                    <Box sx={{ maxWidth: 480 }}>
                        <FieldLabel>Categories</FieldLabel>
                        <MultiStyledAutoComplete
                            options={categories}
                            selected={categories.filter(c => selected.includes(c.id))}
                            onChange={handleCtgChange}
                        />
                    </Box>
                )}
            </ToolbarPaper>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 2 }}
            >
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>Downloaded</Typography>
                <PagerControls params={params} setParams={setParams} totalCount={totalCount}
                    videoLoading={videoLoading} hasVideos={videos.length > 0} />
            </Stack>

            {videoLoading ? (
                <VideoGridSkeleton count={Math.min(params.page_size, 8)} />
            ) : videos.length > 0 ? (
                <Box sx={GRID_SX}>
                    {videos.map((video) => (
                        <Box key={video.id} sx={{ minWidth: 0 }}>
                            <StyledVideoCard
                                video={video}
                                is_admin={props.is_admin}
                                handleDownload={handleDownload(video)}
                                params={params}
                                refreshFnc={RefrashFunc}
                            />
                        </Box>
                    ))}
                </Box>
            ) : (
                <LibraryEmpty
                    filtered={hasFilters}
                    message={hasFilters ? 'No downloads match this category' : 'You have not downloaded any videos yet'}
                    hint={hasFilters ? 'Clear the category filter to see all downloads.' : 'Videos you download from the library will appear here.'}
                    action={!hasFilters && (
                        <LinkButton variant="contained" onClick={() => navigate('/video/library')}>Browse the library</LinkButton>
                    )}
                />
            )}

            <PaginationFoot page={params.page} totalCount={totalCount} pageSize={params.page_size}
                setParams={setParams} loading={videoLoading} />
        </LibraryShell>
    );
};
const mapStatesToProps = (state) => {
    return {
        is_admin: state.user?.is_staff,
    };
};

export default connect(mapStatesToProps, null)(Library);

